import { Building2, Home, HardHat, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { TID } from "@/constants/testIds";

const SERVICES = [
  {
    id: "commercial",
    testid: TID.serviceCommercial,
    Icon: Building2,
    title: "Commercial Cleaning",
    desc: "Recurring, after-hours, and one-time service for offices, retail, medical, and corporate spaces. Detail-driven crews, certified products, full insurance.",
    bullets: ["Day porter & after-hours", "Floor care & disinfection", "Custom recurring schedules"],
    image: "https://images.unsplash.com/photo-1715593949273-09009558300a",
  },
  {
    id: "moveinout",
    testid: TID.serviceMoveinout,
    Icon: Home,
    title: "Apartment Move-In / Move-Out",
    desc: "Deep cleans that turn keys faster — appliance interiors, baseboards, grout, fixtures. Photo-documented and landlord-ready.",
    bullets: ["Inside cabinets & appliances", "Baseboards & blinds", "Photo proof of completion"],
    image: "https://images.pexels.com/photos/19836790/pexels-photo-19836790.jpeg",
  },
  {
    id: "postconstruction",
    testid: TID.servicePostconstruction,
    Icon: HardHat,
    title: "Post-Construction Cleanup",
    desc: "Final clean and rough clean for new builds, renovations, and tenant fit-outs. Dust mitigation, debris removal, and a punch-list polish.",
    bullets: ["Rough, final & touch-up phases", "HEPA dust removal", "Glass, fixtures & site debris"],
    image: "https://images.pexels.com/photos/25533396/pexels-photo-25533396.jpeg",
  },
];

export const Services = () => (
  <section id="services" className="relative py-24 lg:py-32 bg-[#050505]">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="max-w-2xl mb-16">
        <Reveal>
          <div className="overline text-[#D4AF37] mb-4">What We Do</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
            Three specialties.
            <br />
            <span className="gold-grad-text">One standard.</span>
          </h2>
          <p className="mt-6 text-white/65 text-lg leading-relaxed">
            We focus on the work most cleaners avoid — the move-out turnovers, the post-build dust, the late-night office details. Every job, the same elevated standard.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {SERVICES.map((s, idx) => (
          <Reveal
            key={s.id}
            delay={idx * 120}
            className={idx === 1 ? "lg:translate-y-8" : ""}
          >
            <div
              data-testid={s.testid}
              className="card-surface rounded-2xl overflow-hidden flex flex-col h-full"
            >
            <div
              className="h-56 bg-cover bg-center relative"
              style={{ backgroundImage: `url('${s.image}')` }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black" />
              <div className="absolute top-4 left-4 h-10 w-10 rounded-full border border-[#D4AF37]/40 bg-black/60 backdrop-blur flex items-center justify-center">
                <s.Icon size={18} strokeWidth={1.4} className="text-[#D4AF37]" />
              </div>
            </div>
            <div className="p-7 flex flex-col flex-grow">
              <h3 className="font-serif-display text-2xl text-white">{s.title}</h3>
              <p className="mt-3 text-white/65 text-sm leading-relaxed">{s.desc}</p>
              <ul className="mt-5 space-y-2">
                {s.bullets.map((b) => (
                  <li key={b} className="text-sm text-white/70 flex items-start gap-2">
                    <span className="h-1 w-1 rounded-full bg-[#D4AF37] mt-2" />
                    {b}
                  </li>
                ))}
              </ul>
              <a href="#quote" className="mt-7 text-[#D4AF37] text-sm inline-flex items-center gap-1 group">
                Request Quote
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
